import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AppBar,
  Button,
  Box,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { ArrowBack, LocationOnOutlined, Settings } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { AppButton } from '@/shared/components';
import { AddressAutocompleteField } from '@/shared/components/forms';
import publicationService from '../../my-publications/services/publicationService';
import type { MapCityItem } from '../../my-publications/types/publication.types';

const DEFAULT_CENTER: atlas.data.Position = [2.2137, 46.2276];
const DEFAULT_ZOOM = 5;
const DEFAULT_ADDRESS_SEARCH_RADIUS_KM = 50;

interface MapCityCluster {
  representative: MapCityItem;
  cities: MapCityItem[];
  latitude: number;
  longitude: number;
  offersCount: number;
}

interface SearchCenter {
  latitude: number;
  longitude: number;
}

interface MapSearchFormValues {
  address: string;
}

function haversineKm(a: MapCityItem, b: MapCityItem): number {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

function haversineFromCoordinates(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const p1 = toRad(lat1);
  const p2 = toRad(lat2);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

function getClusterRadiusKm(zoom: number): number {
  if (zoom <= 5) return 70;
  if (zoom <= 6) return 45;
  if (zoom <= 7) return 30;
  if (zoom <= 8) return 20;
  if (zoom <= 9) return 14;
  if (zoom <= 10) return 9;
  if (zoom <= 11) return 6;
  if (zoom <= 12) return 4;
  return 2.5;
}

function clusterCitiesByZoom(cities: MapCityItem[], zoom: number): MapCityCluster[] {
  const radiusKm = getClusterRadiusKm(zoom);
  const sorted = [...cities]
    .filter(city => Number.isFinite(city.latitude) && Number.isFinite(city.longitude))
    .sort((a, b) => b.offersCount - a.offersCount);
  const used = new Array(sorted.length).fill(false);
  const clusters: MapCityCluster[] = [];

  for (let i = 0; i < sorted.length; i += 1) {
    if (used[i]) continue;
    const seed = sorted[i];
    const members: MapCityItem[] = [seed];
    used[i] = true;

    for (let j = i + 1; j < sorted.length; j += 1) {
      if (used[j]) continue;
      if (haversineKm(seed, sorted[j]) <= radiusKm) {
        members.push(sorted[j]);
        used[j] = true;
      }
    }

    const representative = [...members].sort((a, b) => {
      if (b.offersCount !== a.offersCount) return b.offersCount - a.offersCount;
      return a.city.localeCompare(b.city);
    })[0];
    const offersCount = members.reduce((sum, member) => sum + member.offersCount, 0);
    const latitude =
      members.reduce((sum, member) => sum + member.latitude * member.offersCount, 0) / offersCount;
    const longitude =
      members.reduce((sum, member) => sum + member.longitude * member.offersCount, 0) / offersCount;

    clusters.push({
      representative,
      cities: members,
      latitude,
      longitude,
      offersCount,
    });
  }

  return clusters.sort((a, b) => b.offersCount - a.offersCount);
}

function createClusterMarker(cluster: MapCityCluster): string {
  const count = String(cluster.offersCount);
  const size = count.length >= 3 ? 34 : 30;
  return `
    <div style="display:flex;align-items:center;justify-content:center;cursor:pointer;">
      <div style="
        background:#1B3A5C;
        color:#fff;
        font-size:11px;
        font-weight:700;
        width:${size}px;
        height:${size}px;
        border-radius:50%;
        border:2px solid #ffffff;
        box-shadow:0 2px 6px rgba(0,0,0,0.25);
        display:flex;
        align-items:center;
        justify-content:center;
        line-height:1;
      ">
        ${cluster.offersCount}
      </div>
    </div>
  `;
}

export function OffersMapPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { control } = useForm<MapSearchFormValues>({
    defaultValues: { address: '' },
  });
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<atlas.Map | null>(null);
  const markersRef = useRef<atlas.HtmlMarker[]>([]);
  const [searchCenter, setSearchCenter] = useState<SearchCenter | null>(null);
  const [searchRadiusKm, setSearchRadiusKm] = useState(DEFAULT_ADDRESS_SEARCH_RADIUS_KM);
  const [radiusDialogOpen, setRadiusDialogOpen] = useState(false);
  const [radiusDraft, setRadiusDraft] = useState(String(DEFAULT_ADDRESS_SEARCH_RADIUS_KM));
  const [cities, setCities] = useState<MapCityItem[]>([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [citiesError, setCitiesError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  const radiusFilteredCities = useMemo(() => {
    if (!searchCenter) return cities;
    return cities.filter((city) => {
      return (
        haversineFromCoordinates(
          city.latitude,
          city.longitude,
          searchCenter.latitude,
          searchCenter.longitude
        ) <= searchRadiusKm
      );
    });
  }, [cities, searchCenter, searchRadiusKm]);

  const clusteredCities = useMemo(
    () => clusterCitiesByZoom(radiusFilteredCities, zoom),
    [radiusFilteredCities, zoom]
  );
  const visibleClusters = useMemo(() => clusteredCities.slice(0, 20), [clusteredCities]);

  useEffect(() => {
    const loadCities = async () => {
      try {
        setLoadingCities(true);
        const response = await publicationService.getMapCities(undefined, 200);
        setCities(response);
        setCitiesError(null);
      } catch (error) {
        console.error('Failed to load map cities:', error);
        setCities([]);
        setCitiesError(t('home.mapPage.errorLoading'));
      } finally {
        setLoadingCities(false);
      }
    };

    void loadCities();
  }, [t]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      return;
    }

    const subscriptionKey = import.meta.env.VITE_AZURE_MAPS_KEY;
    if (!subscriptionKey) {
      console.error('VITE_AZURE_MAPS_KEY is not configured');
      setMapError(true);
      return;
    }

    try {
      const map = new atlas.Map(mapContainerRef.current, {
        authOptions: {
          authType: atlas.AuthenticationType.subscriptionKey,
          subscriptionKey,
        },
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        style: 'road',
        language: 'fr-FR',
      });

      mapRef.current = map;
      map.events.add('ready', () => {
        map.resize();
        setZoom((map.getCamera().zoom as number | undefined) ?? DEFAULT_ZOOM);
        setMapReady(true);
      });
      map.events.add('moveend', () => {
        setZoom((map.getCamera().zoom as number | undefined) ?? DEFAULT_ZOOM);
      });
      map.events.add('error', (errorEvent: unknown) => {
        console.error('Azure Maps error:', errorEvent);
        setMapError(true);
      });
    } catch (error) {
      console.error('Failed to initialize map:', error);
      setMapError(true);
    }

    return () => {
      if (mapRef.current) {
        markersRef.current.forEach(marker => {
          mapRef.current?.markers.remove(marker);
        });
        markersRef.current = [];
        mapRef.current.dispose();
        mapRef.current = null;
      }
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    if (!mapReady || !mapRef.current) {
      return;
    }

    const map = mapRef.current;
    markersRef.current.forEach(marker => map.markers.remove(marker));
    markersRef.current = [];

    visibleClusters.forEach(cluster => {
      const marker = new atlas.HtmlMarker({
        position: [cluster.longitude, cluster.latitude],
        htmlContent: createClusterMarker(cluster),
        pixelOffset: [0, -28],
      });
      map.markers.add(marker);
      markersRef.current.push(marker);

      const markerElement = marker.getElement();
      if (markerElement) {
        markerElement.addEventListener('click', event => {
          event.stopPropagation();
          navigate(
            `/search?fromCityMap=1&city=${encodeURIComponent(cluster.representative.city)}&lat=${cluster.representative.latitude}&lng=${cluster.representative.longitude}`
          );
        });
      }
    });
  }, [mapReady, navigate, visibleClusters]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) {
      return;
    }

    if (searchCenter) {
      mapRef.current.setCamera({
        center: [searchCenter.longitude, searchCenter.latitude],
        zoom: 10,
        type: 'ease',
        duration: 400,
      });
      return;
    }

    const positions: atlas.data.Position[] = cities
      .filter(city => city.latitude != null && city.longitude != null)
      .map(city => [city.longitude, city.latitude]);

    if (positions.length > 0) {
      mapRef.current.setCamera({
        bounds: atlas.data.BoundingBox.fromPositions(positions),
        padding: 60,
      });
      return;
    }

    mapRef.current.setCamera({
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });
  }, [cities, mapReady, searchCenter]);

  const handleRadiusSave = () => {
    const parsed = Number(radiusDraft);
    if (!Number.isFinite(parsed)) {
      setRadiusDraft(String(searchRadiusKm));
      setRadiusDialogOpen(false);
      return;
    }

    const normalized = Math.min(Math.max(Math.round(parsed), 1), 300);
    setSearchRadiusKm(normalized);
    setRadiusDraft(String(normalized));
    setRadiusDialogOpen(false);
  };

  const handleLocateMe = () => {
    if (!mapRef.current || !mapReady) {
      return;
    }

    if (!navigator.geolocation) {
      setLocationError(t('home.mapPage.geolocationUnsupported'));
      return;
    }

    setLocationError(null);
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      position => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;

        mapRef.current?.setCamera({
          center: [longitude, latitude],
          zoom: 12,
          type: 'ease',
          duration: 500,
        });
        setIsLocating(false);
      },
      error => {
        if (error.code === error.PERMISSION_DENIED) {
          setLocationError(t('home.mapPage.geolocationDenied'));
        } else {
          setLocationError(t('home.mapPage.geolocationFailed'));
        }
        setIsLocating(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
      }
    );
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: 'primary.main',
          borderRadius: '0 0 16px 16px',
          zIndex: theme => theme.zIndex.appBar,
          paddingTop: 'env(safe-area-inset-top)',
        }}
        data-testid="offers-map-header"
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            aria-label="back"
            data-testid="offers-map-back"
          >
            <ArrowBack />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              justifyContent: 'center',
              mr: 5,
            }}
          >
            <Typography variant="h6" component="div" sx={{ color: 'white', fontWeight: 600 }}>
              {t('app.title', { defaultValue: 'Bourse aux Matériaux' })}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth={false} sx={{ pt: 2, pb: 10, px: { xs: 2, md: 4 } }}>
        <Paper sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, color: 'primary.main' }}>
            {t('home.mapPage.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('home.mapPage.subtitle')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <AddressAutocompleteField<MapSearchFormValues>
                name="address"
                control={control}
                placeholder={t('home.mapPage.searchPlaceholder')}
                onCoordinatesChange={(lat, lng) => {
                  if (lat !== undefined && lng !== undefined) {
                    setSearchCenter({ latitude: lat, longitude: lng });
                  } else {
                    setSearchCenter(null);
                  }
                }}
              />
            </Box>
            <IconButton
              color="primary"
              onClick={() => {
                setRadiusDraft(String(searchRadiusKm));
                setRadiusDialogOpen(true);
              }}
              aria-label={t('home.mapPage.radiusSettings')}
              sx={{ mt: 0.25 }}
            >
              <Settings />
            </IconButton>
          </Box>
        </Paper>

        <Dialog open={radiusDialogOpen} onClose={() => setRadiusDialogOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>{t('home.mapPage.radiusDialogTitle')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              type="number"
              value={radiusDraft}
              onChange={event => setRadiusDraft(event.target.value)}
              label={t('home.mapPage.radiusLabel')}
              inputProps={{ min: 1, max: 300 }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRadiusDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={handleRadiusSave}>
              {t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>

        <Box sx={{ width: '100%', minHeight: 420, borderRadius: 2, overflow: 'hidden', mb: 2 }}>
          {mapError ? (
            <Alert severity="warning">{t('home.mapPage.mapUnavailable')}</Alert>
          ) : (
            <Box
              ref={mapContainerRef}
              sx={{
                width: '100%',
                height: '65vh',
                minHeight: 420,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            />
          )}
        </Box>

        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="h6" fontWeight={700}>
              {t('home.mapPage.nearbyTitle')}
            </Typography>
            <AppButton
              variant="text"
              size="small"
              onClick={handleLocateMe}
              disabled={!mapReady || isLocating}
              startIcon={<LocationOnOutlined sx={{ fontSize: 18 }} />}
            >
              {t('home.mapPage.aroundMe')}
            </AppButton>
          </Box>

          {locationError && <Alert severity="warning" sx={{ mb: 1.5 }}>{locationError}</Alert>}

          {loadingCities && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          )}

          {citiesError && !loadingCities && <Alert severity="error">{citiesError}</Alert>}

          {!loadingCities && !citiesError && visibleClusters.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('home.mapPage.empty')}
            </Typography>
          )}

          {!loadingCities && !citiesError && visibleClusters.length > 0 && (
            <List disablePadding>
              {visibleClusters.map(cluster => (
                <ListItem
                  key={`${cluster.representative.city}-${cluster.latitude}-${cluster.longitude}`}
                  disablePadding
                  sx={{ py: 0.8, borderBottom: '1px solid', borderColor: 'divider' }}
                  secondaryAction={
                    <AppButton
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        navigate(
                          `/search?fromCityMap=1&city=${encodeURIComponent(cluster.representative.city)}&lat=${cluster.representative.latitude}&lng=${cluster.representative.longitude}`
                        )
                      }
                    >
                      {t('home.mapPage.openSearch')}
                    </AppButton>
                  }
                >
                  <ListItemText
                    primary={cluster.representative.city}
                    secondary={t('home.mapPage.cityOffers', { count: cluster.offersCount })}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
